/**
 * ExtensionBanner Component
 * Promotes Chrome extension installation to Chrome users
 * Only shows on Chrome browsers, dismissable with localStorage persistence
 */

'use client';

import { useState, useEffect } from 'react';

// Replace with your actual Chrome Web Store URL after publishing
const CHROME_STORE_URL = 'https://chrome.google.com/webstore/detail/can-i-use-my-fsa-checker/YOUR_EXTENSION_ID';
const STORAGE_KEY = 'extension_banner_dismissed';

interface ExtensionBannerProps {
  variant?: 'hero' | 'inline' | 'floating';
}

export default function ExtensionBanner({ variant = 'inline' }: ExtensionBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isChrome, setIsChrome] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if running in browser
    if (typeof window === 'undefined') return;

    // Detect Chrome browser
    const userAgent = navigator.userAgent.toLowerCase();
    const isChromeDevice = userAgent.includes('chrome') && !userAgent.includes('edg');
    setIsChrome(isChromeDevice);

    // Check if banner was dismissed
    const isDismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    
    // Show banner after a short delay for better UX
    if (isChromeDevice && !isDismissed) {
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 1000);
    }
  }, []);

  const handleDismiss = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      localStorage.setItem(STORAGE_KEY, 'true');
    }, 300);
  };

  const handleInstall = () => {
    window.open(CHROME_STORE_URL, '_blank');
  };

  // Don't render if not Chrome or already dismissed
  if (!isVisible || !isChrome) {
    return null;
  }

  // Floating variant - fixed at bottom of screen
  if (variant === 'floating') {
    return (
      <div 
        className={`fixed bottom-4 left-4 right-4 z-50 transition-all duration-300 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="max-w-2xl mx-auto">
          <div className="relative bg-gradient-to-r from-primary-500 via-primary-400 to-primary-500 rounded-2xl shadow-2xl p-1">
            <div className="bg-white rounded-xl p-4 sm:p-5">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className="hidden sm:flex w-14 h-14 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl items-center justify-center flex-shrink-0 shadow-lg">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                    Check eligibility while you shop! 🛒
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                    One-click FSA/HSA checks on Amazon, Walmart & more
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleInstall}
                    className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    Install
                  </button>
                  <button
                    onClick={handleDismiss}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Dismiss"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Hero variant - large banner for landing page
  if (variant === 'hero') {
    return (
      <div 
        className={`relative overflow-hidden transition-all duration-500 ${
          isAnimating ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'
        }`}
      >
        <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 py-6 sm:py-8">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary-400/30 rounded-full blur-3xl"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              {/* Left content */}
              <div className="flex items-center gap-5">
                {/* Extension icon */}
                <div className="relative">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                    <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-primary-600 text-xs font-bold">✓</span>
                  </div>
                </div>

                {/* Text */}
                <div className="text-center sm:text-left">
                  <h3 className="text-amber-300 font-bold text-lg sm:text-xl mb-1">
                    Get the Chrome Extension
                  </h3>
                  <p className="text-white text-sm sm:text-base max-w-md">
                    Check FSA/HSA eligibility instantly while browsing Amazon, Walmart, Target & more
                  </p>
                </div>
              </div>

              {/* Right actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleInstall}
                  className="group flex items-center gap-2 bg-white text-primary-600 px-6 py-3 rounded-xl font-bold hover:shadow-xl hover:scale-105 transition-all duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>Install Free</span>
                  <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
                <button
                  onClick={handleDismiss}
                  className="p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
                  aria-label="Dismiss"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Inline variant - card style for results page
  return (
    <div 
      className={`transition-all duration-500 ${
        isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-primary-50 rounded-2xl border border-primary-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-primary-400/10 rounded-bl-full"></div>
        
        <div className="relative p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                  </svg>
                </div>
                {/* Badge */}
                <div className="absolute -top-2 -right-2 bg-primary-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  FREE
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                🛒 Shop smarter with our Chrome Extension
              </h3>
              <p className="text-gray-600 mb-4">
                Get instant FSA/HSA eligibility checks while browsing Amazon, Walmart, CVS, and more. 
                No need to copy & paste – just click and know!
              </p>
              
              {/* Features */}
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <svg className="w-3.5 h-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Auto-detects products
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <svg className="w-3.5 h-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  One-click results
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                  <svg className="w-3.5 h-3.5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Works on any site
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex sm:flex-col items-center gap-3 w-full sm:w-auto">
              <button
                onClick={handleInstall}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Add to Chrome</span>
              </button>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 text-sm font-medium hover:underline transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

